<template>
  <div>
    <v-container fluid class="content-wrapper pt-2 pt-md-10 px-4 px-md-8">
      <h2 class="primary300--text">Help</h2>
      <p>Version: {{ this.version }}</p>

      <v-expansion-panels id="expansion-panels-help">
        <v-expansion-panel class="mb-4">
          <v-expansion-panel-header>
            Scope
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <p>
              The aim of the ResearchHUB is to materialise the Unified Policy to Support Open
              Research in Abu Dhabi, serving national research infrastructures and allowing
              scientific communities to share and process research results and data in various
              scientific domains in Abu Dhabi and beyond. This has been achieved via the open,
              trusted and user-friendly ResearchHUB Catalogue and Marketplace to a harmonised and
              aggregated pool of available research infrastructures and services in Abu Dhabi,
              making them easily findable and accessible by researchers, developers, innovators,
              citizens, organisations, businesses, funders and policy makers. This is an initiative
              of the Advanced Technology Research Council (ATRC).
            </p>
            <p>
              The ResearchHUB is populated with data provided by Service Providers on voluntary
              basis and it is intended to be enriched and populated with additional information with
              a long-term objective to progressively be able to offer a complete Catalogue and
              Marketplace for research in Abu Dhabi and beyond.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel class="mb-4">
          <v-expansion-panel-header>
            Overview
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <p>
              The ResearchHUB is an online Catalogue and Marketplace
              <b>openly available to all interested users</b>. It offers rich browsing
              functionalities including keyword search and filtering options by Hosting
              Institutions, Service Providers, Scientific Disciplines, Type of Services, etc. The
              user interface has been designed to be intuitive and does not require any particular
              knowledge.
            </p>
            <p>
              Additional functionalities, such us the possibility to order a service, as part of the
              marketplace, are available to <b>Registered Users</b> through an authentication
              interface. This functionality enables users to contact Service Providers and initiate
              a request for the delivery of a service; access a dashboard, receive notifications
              etc. This interface uses institutional or social media identification and is therefore
              simple to use. It is recommended, especially to regular users, to register to be able
              to access these additional functionalities and record their activity.
            </p>
            <p>
              <b>Service Providers</b> are research units of universities or research organisations
              offering their Services through the ResearchHUB. To register their services, the
              Hosting Organisation needs to identify an authorized representative who will be
              responsible for entering the information in the ResearchHUB and updating it as
              necessary. The onboarding of the Service Providers and their Services is a
              step-by-step process, guided through the interface by filling-in a set of online forms
              which are accompanied by instructions and definitions. The full description of the
              information to be entered is available in the ResearchHUB Data Model.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel class="mb-4">
          <v-expansion-panel-header>
            Quality Assurance
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <p>
              The information included in the ResearchHUB is the responsibility of the Service
              Providers. When entering the information, Service Providers are strongly encouraged to
              present their Services in a way that will be comprehensible not only to specialised
              individuals but also to academic or industrial users from another research field or a
              non-specialist.
            </p>
            <p>
              The Service Provider is therefore advised to focus on <b>what can be achieved</b> with
              a specific Service, technique/instrument and
              <b>what are the benefits for the users</b>, rather than on the description of the
              technique or instrument. Specific equipment can be described in more details in a
              separate designated field.
            </p>
            <p>
              Information on <b>modalities and prerequisites to access a service</b> should also be
              included to the extent possible, to allow a potential user to evaluate the possibility
              to benefit from a service independently of her/his geographical location. This may
              also be of particular relevance for industrial users in the region or elsewhere.
            </p>
            <p>
              The team of ATRC provides training and support to Service Providers to assist them
              during the registration process and advise them on the best practices for Service
              onboarding so as to ensure that the end result is in line with the expectations.
            </p>
            <p>
              The ATRC team may conduct periodic audits of the different records and contact the
              Service Providers individually for suggesting improvements or corrections.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel class="mb-4">
          <v-expansion-panel-header>
            Feedback and Future Developments
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            The ResearchHUB has been developed based on the experience of the ATRC team and an
            international team of specialists, following the current state-of-the-art technologies
            in this field. However, as developments occur quickly, it is expected that upgrades,
            improvements and additions may be necessary. We would like to encourage all users of the
            ResearchHUB to provide regular feedback via the available Feedback functionality to
            ensure that the ResearchHUB evolves according to their needs.
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel class="mb-4">
          <v-expansion-panel-header>
            Q&A
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <h5>What is considered a Service Provider?</h5>
            <p>
              A Service Provider in the ResearchHUB is a Research Lab under a specific Hosting
              Institution offering Services through the ResearchHUB Catalogue and Marketplace.
            </p>
            <h5>Why to become a Provider in ResearchHUB?</h5>
            <p>
              You may benefit in multiple ways by becoming a Provider in ResearchHUB. Indicatively
              you may:
            </p>
            <div>
              <ul>
                <li>Improve discoverability & visibility of offered Services;</li>
                <li>Expand the users’ base to include more research communities;</li>
                <li>Attract potential users from both academia and industry;</li>
                <li>Enhance collaboration and networking across the wider scientific community;</li>
                <li>Optimize service portfolio by identifying overlapping activities or gaps;</li>
                <li>
                  Showcase services in a format compatible with EU initiatives (i.e. the European
                  Open Science Cloud-EOSC) and be ready for interoperating with other research
                  communities worldwide.
                </li>
              </ul>
            </div>
            <br />
            <h5>Registration</h5>
            <p>
              Click on the "Log In" button available at any time at the top right corner. You can
              register either as a Service Provider or just as an End-User with your email address
              and a password of your choice. There is also the option to register with your social
              media credentials. We recommend though to use your institutional email. Then you are
              redirected to your Dashboard which includes several features.
            </p>
            <h5>Become a Service Provider</h5>
            <p>
              After registering as a Provider, you need to login using your credentials and: 1. From
              your Dashboard, click on your profile icon on the top right corner 2. Select the “Add
              new Service Provider” and continue by filling in the forms that will appear. 3. In
              case you have already registered your lab, you may update the information by clicking
              on the “Provider Settings”.
            </p>
            <h5>Add Services</h5>
            <p>
              At any time, you may add your offered Services; From your Dashboard, click on your
              profile icon on the top right corner and select the Provider for which you wish to add
              Services. Then on menu on the left side of the screen, you may select “Add Services”
              and continue by filling in the forms that will appear. You can update your Services
              information at any time by selecting “Your Services” from the menu on your left side
              of the screen, then select the specific service from the list and click “Edit”. Then
              the forms with the filled in information will appear including some additional
              optional fields that we recommend filling in as they add more useful content to your
              Service in the ResearchHUB Catalogue and Marketplace.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel class="mb-4">
          <v-expansion-panel-header>
            The Standard Template for Collaboration Agreement
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pt-4">
            <a @click="downloadFile('Template Collaboration Agreement.docx')"
              >ATRC Platform - Template Collaboration Agreement.docx</a
            >
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'Help',
  data() {
    return {
      version: process.env.VUE_APP_VERSION,
    };
  },
  methods: {
    downloadFile(file) {
      this.axios({
        url: 'api/v1/files/' + file,
        method: 'GET',
        responseType: 'blob', // important
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file);
        document.body.appendChild(link);
        link.click();
      });
    },
  },
};
</script>

<style scoped>
#expansion-panels-help .v-expansion-panel::after {
  border-top: none;
}
</style>
